import React, {createRef, useEffect, useMemo, useState} from 'react';
import Image from 'next/image';
import Head from 'next/head';
import { useWindowWidth } from '@react-hook/window-size';
import { DestinationItem, SearchBox } from '../index';
import cn from 'classnames';
import styles from './LandingPage.module.scss';
import { CustomLeftArrow, CustomRightArrow } from '../Carousel/arrows/arrows';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import dynamic from 'next/dynamic';
import { ReviewItem } from '../Lists/ReviewItem';
import { Currency } from './Currency/Currency';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { addDays, format } from 'date-fns';
import RecommendationsList from '../RecommendationsList/RecommendationsList';
import parse from 'html-react-parser';
import { checkUserCountry } from '../../utils/checkUserCountry';
import { OffersViews } from '../OffersViews/OffersViews';

const CallToSignUp = dynamic(() => import('../CallToSignUp/CallToSignUp'));
const ScrollableContent = dynamic(() =>
  import('../ScrollableContent/ScrollableContent')
);
const RegionalDealsTable = dynamic(() => import('../DealsTable/RegionalDealsTable/RegionalDealsTable'), {
  ssr: false,  // Disable server-side rendering for this component
});
// const FeaturedContent = dynamic(() =>
//   import('../FeaturedContent/FeaturedContent')
// );

const FeaturedContent = dynamic(() =>
  import('../FeaturedContent/FeaturedContent')
);
const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 2,
    slidesToSlide: 2,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 2,
    slidesToSlide: 2,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

export const LandingPage = ({
  //setLoginDialogState,
  landing,
  authCompleted,
  yrWeatherWidgetURL,
  currency,
  recommendation,
  selfUrl,
  isPreview = false,
}) => {
  const userStore = useSelector((state) => state.user.value);
  const width = useWindowWidth();
  const [carouselHeight, setCarouselHeight] = useState(0);
  const [searchBoxState, setSearchBoxState] = useState(null);
  const [dealsAvailable, setDealsAvailable] = useState(false);
  const [weatherIframeWidth, setWeatherIframeWidth] = useState('100%');
  const carousel = createRef();
  const origin =
    landing.cityFrom || landing.countryFrom || landing.regionFrom || null;
  const destination = landing.cityTo || landing.countryTo || landing.regionTo;
  const destinationCode = landing.cityTo?.code || null;
  const originCountryCode = landing.cityFrom?.countryCode || null;
  const originCode = landing.cityFrom?.code || null;
  const originCurrency = landing.cityFrom?.currency || null;
  const options = {
    replace: (domNode) => {
      if (domNode.type === 'tag' && domNode.name === 'a') {
        if (domNode.attribs?.href?.includes('http')) {
          if (domNode.attribs?.target === '_blank') {
            domNode.attribs.target = '_blank';
          }
         
          if (domNode.attribs?.rel?.includes('sponsored')) {
            domNode.attribs.rel = 'noreferrer noopener sponsored';
          } else {
            domNode.attribs.rel = 'noreferrer noopener nofollow';
          }
        }
      }
    },
  };
  const replaceContent = (content) => parse(content, options);
  const defaultOrigin = useMemo(() => ({
    country: originCountryCode || userStore?.country,
    origin: originCode,
  }), [userStore?.country]);


  useEffect(() => {
    setCarouselHeight(carousel.current?.offsetWidth / 1.3);
    setWeatherIframeWidth(width > 1024 ? '800px' : '100%');
  }, [carousel, width]);
  useEffect(() => {
    if (destination) {
      const getCabinClassCode = (textId) => {
        switch (textId) {
          case 'business-class':
            return 'BIZ';
          case 'premium-economy-class':
            return 'FLX';
          case 'first-class':
            return 'FST';
          default:
            return 'ECO';
        }
      };
      setSearchBoxState({
        from: origin?.name + ' ' + origin?.countryName || null,
        exactFrom: false,
        to: destination?.name + ' ' + destination?.countryName || null,
        exactTo: false,
        tripState: 'round_trip',
        cabinClass: getCabinClassCode(landing.textIdClass),
        dateRange: {
          departure: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
          return: format(addDays(new Date(), 8), 'yyyy-MM-dd'),
        },
        departure: null,
        airlineType: 'all',
        passengers: {
          adults: 1,
        },
        multi_city: [
          {
            from: null,
            to: null,
            departure: null,
            airlineType: 'all',
          },
        ],
      });
    }
  }, [landing]);

  const generateCabinClass = () => {
    switch (landing.textIdClass) {
      case 'economy-class':
        return { name: 'Economy Class', code: 'ECO' };
      case 'premium-economy-class':
        return { name: 'Premium Economy Class', code: 'FLX' };
      case 'business-class':
        return { name: 'Business Class', code: 'BIZ' };
      case 'first-class':
        return { name: 'First Class', code: 'FST' };
    }
  };

  return (
    <>
      {!isPreview && (
        <Head>
          <meta name="robots" content="index, follow" />
          {origin?.name ? (
            <title>
              Cheap {generateCabinClass().name} Flights from {origin.name} to{' '}
              {destination.name} - First-Class.com
            </title>
          ) : (
            <title>
              Cheap {generateCabinClass().name} Flights to {destination.name} -
              First-Class.com
            </title>
          )}

          <link
            rel="canonical"
            href={`${process.env.apiUrl}/flights/${selfUrl.join('/')}`}
          />
          <meta
            name="description"
            content={
              !origin?.name
                ? `Search compare, and book the best ${generateCabinClass().name} flight deals to ${
                    destination.name
                  }.  Secure the cheapest, and fastest round-trip business class tickets to ${
                    destination.name
                  }.`
                : `Search and compare the best deals on ${generateCabinClass().name} flights from ${
                    origin.name
                  } to ${
                    destination.name
                  }. Search 100+ sites & book your ${generateCabinClass().name} tickets from ${
                    origin.name
                  } to ${destination.name}`
            }
          />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content="first-class.com" />
          {origin?.name ? (
            <meta
              name="twitter:title"
              content={`Cheap ${generateCabinClass().name} Flights 
            ${origin.name} to ${destination.name} -
            First-Class.com`}
            />
          ) : (
            <meta
              name="twitter:title"
              content={`Cheap ${generateCabinClass().name} Flights to ${
                destination.name
              } -
            First-Class.com`}
            />
          )}
          <meta
            name="twitter:description"
            content={
              !origin?.name
                ? `Search compare, and book the best ${generateCabinClass().name} flight deals to ${
                    destination.name
                  }.  Secure the cheapest, and fastest round-trip business class tickets to ${
                    destination.name
                  }.`
                : `Search and compare the best deals on ${generateCabinClass().name} flights from ${
                    origin.name
                  } to ${
                    destination.name
                  }. Search 100+ sites & book your ${generateCabinClass().name} tickets from ${
                    origin.name
                  } to ${destination.name}`
            }
          />
          <meta
            name="twitter:image"
            content={process.env.mediaUrl + landing.image?.replace('/', '')}
          />
          {origin?.name ? (
            <meta
              property="og:title"
              content={`Cheap ${generateCabinClass().name} Flights 
            ${origin.name} to ${destination.name} -
            First-Class.com`}
            />
          ) : (
            <meta
              property="og:title"
              content={`Cheap ${generateCabinClass().name} Flights to ${
                destination.name
              } -
            First-Class.com`}
            />
          )}
          <meta property="og:type" content="article" />
          <meta
            property="og:image"
            content={process.env.mediaUrl + landing?.image?.replace('/', '')}
          />
          <meta
            property="og:description"
            content={
              !origin?.name
                ? `Search compare, and book the best ${generateCabinClass().name} flight deals to ${
                    destination.name
                  }.  Secure the cheapest, and fastest round-trip business class tickets to ${
                    destination.name
                  }.`
                : `Search and compare the best deals on ${generateCabinClass().name} flights from ${
                    origin.name
                  } to ${
                    destination.name
                  }. Search 100+ sites & book your ${generateCabinClass().name} tickets from ${
                    origin.name
                  } to ${destination.name}`
            }
          />
          {(landing.questionsAnswers?.length ||
            landing.questionsAnswersDirection?.length ||
            landing.questionsAnswersDestination) && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: landing.questionsAnswers?.length
                  ? JSON.stringify({
                      mainEntity: landing.questionsAnswers.map((item) => ({
                        name: item.question,
                        acceptedAnswer: {
                          text: item.answer,
                          '@type': 'Answer',
                        },
                        '@type': 'Question',
                      })),
                      '@type': 'FAQPage',
                      '@context': 'http://schema.org',
                    })
                  : landing.questionsAnswersDirection?.length
                  ? JSON.stringify({
                      mainEntity: landing.questionsAnswersDirection.map(
                        (item) => ({
                          name: item.question,
                          acceptedAnswer: {
                            text: item.answer,
                            '@type': 'Answer',
                          },
                          '@type': 'Question',
                        })
                      ),
                      '@type': 'FAQPage',
                      '@context': 'http://schema.org',
                    })
                  : landing.questionsAnswersDestination?.length
                  ? JSON.stringify({
                      mainEntity: landing.questionsAnswersDestination.map(
                        (item) => ({
                          name: item.question,
                          acceptedAnswer: {
                            text: item.answer,
                            '@type': 'Answer',
                          },
                          '@type': 'Question',
                        })
                      ),
                      '@type': 'FAQPage',
                      '@context': 'http://schema.org',
                    })
                  : null,
              }}
            />
          )}
        </Head>
      )}
      <div className={cn(styles.landing)}>
        <div className={cn(styles.topSection, dealsAvailable ? styles.topDeals : '')}>
          <Image
            className="vjs-tech"
            src={process.env.mediaUrl + landing.image?.replace('/', '')}
            alt="Top image"
            layout={'fill'}
            objectFit={'cover'}
          />
          <h1 className={cn(styles.topSectionText, 'font-fragment', dealsAvailable ? styles.topDeals : '' )}>
            <span> {generateCabinClass().name} </span> flights
            {origin?.name ? ' from ' + origin.name : ''}
            {destination && ' to ' + destination.name}
          </h1>
          {userStore && userStore.country && checkUserCountry(originCountryCode || userStore.country) && destinationCode && (
            <div className={cn(styles.dealsTable, 'container')}>
              <RegionalDealsTable
                country={originCountryCode || userStore.country}
                currency={originCurrency || userStore.currency}
                origin={originCode}
                destination={destinationCode}
                cabinClass={generateCabinClass().code}
                cabinClassTitle={generateCabinClass().name}
                intervalSeconds={7}
                setDealsAvailable={setDealsAvailable}>  
              </RegionalDealsTable>
            </div>
          )}  
        </div>

        <div className="container">
          <div className={styles.content}>
            <div
              className={cn(styles.topSectionSearch, 'container mx-auto px-4', dealsAvailable ? styles.topDeals : '')}
            >
              {width >= 1024 && (
                <SearchBox
                  type={'mainSearch'}
                  activeTab={0}
                  flightSearchValue={searchBoxState}
                  searchSource={'Flights landing page'}
                />
              )}
            </div>
            {width < 1024 && (
              <div className={cn(styles.contentSearch, 'mx-auto')}>
                <SearchBox
                  type={'sideSearch'}
                  activeTab={0}
                  flightSearchValue={searchBoxState}
                  searchSource={'Flights landing page'}
                />
              </div>
            )}
          </div>

          <div className='container'>
            <OffersViews
              defaultOrigin={defaultOrigin}
              defaultDestination={{
                destination: destinationCode,
              }}
              defaultCabinClass={generateCabinClass().code}
              filtersShown={false}
              searchSource="Offer on Flights landing page"
              customHeader={
                <h2 className='font-libre text-xl my-5 lg:my-10'>
                  Latest {generateCabinClass().name} round-trip flight deals {origin?.name ? ' from ' + origin.name : ''}
                  {destination && ' to ' + destination.name}
                </h2>
              }>
            </OffersViews>
          </div>
          <div className={styles.contentBox}>
            {!!landing?.content && (
              <div
                className={cn(
                  styles.introductionDescription,
                  'font-libre mt-8'
                )}
              >
                {replaceContent(landing.content)}
              </div>
            )}
            {!!landing.questionsAnswers?.length && (
              <div className="flex flex-col justify-center items-center sm:px-5 my-8">
                <h3 className={cn(styles.listTitle)}>
                  FAQs - Booking &nbsp;{destination.name} &nbsp;
                  <span>{generateCabinClass().name} </span> &nbsp;Flights
                </h3>
                {landing.questionsAnswers.map((faq) => (
                  <ScrollableContent
                    key={faq.id}
                    heading={faq.question}
                    content={faq.answer}
                    style={styles.scrollableContent}
                  />
                ))}
              </div>
            )}
          </div>
          {!!landing.contentDirection && (
            <div className={styles.contentBox}>
              <h3
                className={cn(styles.introductionHeading, 'font-fragment mt-7')}
              >
                {origin?.name} to {destination?.name} direction
              </h3>
              <div className={cn(styles.introductionDescription, 'font-libre')}>
                {replaceContent(landing.contentDirection)}
              </div>
              {!!landing.questionsAnswersDirection?.length && (
                <div className="flex flex-col justify-center items-center sm:px-5 my-8">
                  <h3 className={cn(styles.listTitle)}>
                    {origin.name} to {destination?.name} flight FAQs
                  </h3>
                  {landing.questionsAnswersDirection.map((faq) => (
                    <ScrollableContent
                      key={faq.id}
                      heading={faq.question}
                      content={faq.answer}
                      style={styles.scrollableContent}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {!!landing.destinations?.length && (
            <div
              className={cn(styles.contentBox, 'flex items-center flex-col')}
            >
              <DestinationItem item={landing.destinations[0]} />
            </div>
          )}
        </div>
        {!!landing.articles?.length && (
          <div className={styles.darkContainer}>
            <InView triggerOnce>
              {({ inView, ref }) => (
                <div ref={ref} className="container mx-auto px-4 lg:px-0">
                  {inView && (
                    <FeaturedContent
                      items={landing.articles}
                      enableInfinite={true}
                    />
                  )}
                </div>
              )}
            </InView>
          </div>
        )}
        <div className="container mt-4">
          <div className={styles.contentBox}>
            {!!landing.contentDestination && (
              <>
                <div
                  className={cn(
                    styles.introductionDescription,
                    'font-libre mt-7'
                  )}
                >
                  {replaceContent(landing.contentDestination)}
                </div>
              </>
            )}
            {!!landing.airlines?.length && (
              <>
                <h3 className={cn(styles.listTitle)}>
                  Airlines that fly direct
                </h3>
                <div className={styles.airlinesContainer}>
                  {landing.airlines.map((airline) => {
                    return (
                      <div className={styles.airline} key={airline.name}>
                        <img
                          src={airline.logo}
                          alt={airline.name}
                          className={styles.airlineLogo}
                        />
                        <div className={styles.airlineName}>{airline.name}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {!!landing.airports?.length && (
              <>
                <h3 className={cn(styles.listTitle)}>
                  Airports in {destination.name}
                </h3>
                <div className={styles.airlinesContainer}>
                  {landing.airports.map((airport) => {
                    return (
                      <ul className={styles.airline} key={airport.id}>
                        <li className={styles.airlineName}>
                          {airport.name} ({airport.code})
                        </li>
                      </ul>
                    );
                  })}
                </div>
              </>
            )}
            {!!destination?.currency && currency?.Rate && (
              <div className={'mb-4'}>
                <Currency currency={currency} destination={destination} />
              </div>
            )}
          </div>
          {yrWeatherWidgetURL && (
            <>
              <iframe
                src={yrWeatherWidgetURL}
                width={weatherIframeWidth}
                height="530px"
                style={{
                  borderRadius: '12px',
                  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05))',
                  margin: 'auto',
                }}
              />
            </>
          )}
          {!yrWeatherWidgetURL && isPreview && (
            <div>Weather forecast unavailable for preview</div>
          )}
          <div className={styles.contentBox}>
            {!!landing.questionsAnswersDestination?.length && (
              <div className="flex flex-col justify-center items-center sm:px-5 my-8">
                <h3 className={cn(styles.listTitle)}>
                  {destination?.name} FAQs
                </h3>
                {landing.questionsAnswersDestination.map((faq) => (
                  <ScrollableContent
                    key={faq.id}
                    heading={faq.question}
                    content={faq.answer}
                    style={styles.scrollableContent}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {/*
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-x-10">
          {!!landing.destinations?.length && (
            <div className="">
              <DestinationItem item={landing.destinations[0]} />
            </div>
          )}

          {!!landing.articles?.length && (
            <div className="">
              <RecommendationsList
                items={landing.articles}
                hideHeading
              />
            </div>
          )}
        </div>
      </div> */}

        {!!landing.hotels?.content?.length && (
          <div className={cn(styles.darkContainer, 'mt-10')}>
            <div className={cn(styles.hotelsOverflow, 'container pb-8')}>
              <h3
                className={cn(
                  styles.heading,
                  'text-center text-white font-fragment mb-10'
                )}
              >
                Luxury Hotels in {destination?.name}
              </h3>
              <Carousel
                infinite={true}
                responsive={responsive}
                autoPlaySpeed={5000}
                containerClass={styles.carouselContainer}
                customLeftArrow={
                  <CustomLeftArrow
                    left={width < 1024 ? 10 : 0}
                    position={'absolute'}
                  />
                }
                customRightArrow={
                  <CustomRightArrow
                    right={width < 1024 ? 10 : 0}
                    position={'absolute'}
                  />
                }
              >
                {landing.hotels?.content?.map((item) => {
                  if (item.image?.[0] === '/') {
                    item.image = item.image?.replace('/', '');
                  }
                  return (
                    <div
                      key={item.textId}
                      className="mx-10 bg-white"
                      style={{ height: carouselHeight }}
                    >
                      <ReviewItem
                        type="Hotel"
                        starRating={true}
                        review={item}
                        url={`/hotel/review/${item.textId}`}
                      />
                    </div>
                  );
                })}
              </Carousel>
            </div>
          </div>
        )}

        {!!landing.popularDestination?.length && (
          <div className="container">
            <div className={styles.contentBox}>
              <h3 className={cn(styles.listTitle)}>Popular flight roots</h3>
              <div className={styles.popularDestinationsContainer}>
                {landing.popularDestination.map((destination) => {
                  return (
                    <div
                      className={styles.popularDestination}
                      key={destination.directionId}
                    >
                      <div className={styles.popularDestinationName}>
                        {origin.name}
                      </div>
                      <ArrowRightAltIcon />
                      <div className={styles.popularDestinationName}>
                        {destination.cityTo}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* {!!landing.questionsAnswers?.length && (
            <div className="flex flex-col justify-center items-center sm:px-5 my-8">
              <h3
                className={cn(styles.listTitle, 'text-3xl font-fragment mb-4')}
              >
                FAQs
              </h3>
              {landing.questionsAnswers.map((faq) => (
                <ScrollableContent
                  key={faq.id}
                  heading={faq.question}
                  content={faq.answer}
                  style={styles.scrollableContent}
                />
              ))}
            </div>
          )} */}

              {/* {!!landing.questionsAnswersDirection?.length && (
            <div className="flex flex-col justify-center items-center sm:px-5 my-8">
              <h3
                className={cn(styles.listTitle, 'text-3xl font-fragment mb-4')}
              >
                {landing?.cityFrom?.name} to {landing?.cityTo?.name} flight
                FAQs
              </h3>
              {landing.questionsAnswersDirection.map((faq) => (
                <ScrollableContent
                  key={faq.id}
                  heading={faq.question}
                  content={faq.answer}
                  style={styles.scrollableContent}
                />
              ))}
            </div>
          )} */}

              {/* {!!landing.questionsAnswersDestination?.length && (
            <div className="flex flex-col justify-center items-center sm:px-5 my-8">
              <h3
                className={cn(styles.listTitle, 'text-3xl font-fragment mb-4')}
              >
                {landing?.cityTo?.name} FAQs
              </h3>
              {landing.questionsAnswersDestination.map((faq) => (
                <ScrollableContent
                  key={faq.id}
                  heading={faq.question}
                  content={faq.answer}
                  style={styles.scrollableContent}
                />
              ))}
            </div>
          )} */}
            </div>
          </div>
        )}

        {userStore && !userStore.userid && (
          <InView triggerOnce>
            {({ inView, ref }) => (
              <div ref={ref} className="w-full mx-auto mb-20 sm:mb-10">
                {inView && (
                  <CallToSignUp
                    country={userStore?.country}
                    authCompleted={authCompleted}
                    sourcePage={generateCabinClass().name + ' FlightLandingPage'}
                  />
                )}
              </div>
            )}
          </InView>
        )}
        {!isPreview && (
          <div className="container flex justify-center items-center sm:w-full mt-20">
            <RecommendationsList items={recommendation} />
          </div>
        )}
      </div>
    </>
  );
};
